import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowRight, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import api, { readError } from "@/lib/api";

const LOCALES = ["hr", "en", "de", "fr"];
const LOCALE_LABELS = { hr: "Hrvatski", en: "English", de: "Deutsch", fr: "Français" };

export default function AdminPages() {
  const [locale, setLocale] = useState("hr");
  const [pages, setPages] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setPages(null);
    api.get("/admin/pages", { params: { locale } }).then((r) => {
      if (!cancelled) setPages(r.data);
    }).catch((err) => {
      if (!cancelled) setError(readError(err, "Could not load pages."));
    });
    return () => { cancelled = true; };
  }, [locale]);

  return (
    <div className="space-y-8 animate-fade-up">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-primary">CMS</div>
        <h1 className="mt-1 font-serif text-4xl text-foreground">Pages</h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          Edit page text, headings, and SEO metadata for every locale. Translations are stored independently.
          Edit Croatian first; you can copy HR content to EN/DE/FR with one click inside the editor.
        </p>
      </div>

      {/* Locale tabs */}
      <div className="inline-flex rounded-lg border border-border bg-card p-1" data-testid="pages-locale-tabs">
        {LOCALES.map((l) => (
          <button
            key={l}
            type="button"
            data-testid={`locale-tab-${l}`}
            onClick={() => setLocale(l)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              locale === l ? "bg-primary text-primary-foreground shadow-interactive" : "text-foreground/70 hover:bg-muted"
            }`}
          >
            {LOCALE_LABELS[l]}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5" strokeWidth={1.5} />
          {error}
        </div>
      )}

      <Card className="bg-card border-border/60 shadow-ambient overflow-hidden">
        <CardContent className="p-0">
          {pages === null ? (
            <div className="p-10 flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading pages…
            </div>
          ) : pages.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">No pages found for this locale.</div>
          ) : (
            <ul className="divide-y divide-border/60" data-testid="pages-list">
              {pages.map((p) => (
                <li key={`${p.id}-${p.locale}`} className="px-5 py-4 hover:bg-muted/40 transition-colors">
                  <Link
                    to={`/admin/pages/${p.id}?locale=${p.locale}`}
                    data-testid={`page-row-${p.id}`}
                    className="flex items-center gap-4"
                  >
                    <div className="rounded-lg bg-primary/10 p-2">
                      <FileText className="h-4 w-4 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <div className="font-serif text-lg text-foreground">{p.id}</div>
                        {p.auto_copied && (
                          <span className="text-[10px] uppercase tracking-[0.15em] rounded-full bg-secondary/15 text-secondary px-2 py-0.5">
                            auto-copied
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-sm text-foreground/75 line-clamp-1">{p.meta_title}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground line-clamp-1">{p.meta_description}</div>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{(p.sections || []).length} sections</span>
                      <CheckCircle2 className="h-3.5 w-3.5 text-primary/60" strokeWidth={1.5} />
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
